// SetPassword.js v3.0 - New Hire Welcome Experience
// Branded welcome page for new team members setting up their account
import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, Sun, Moon, CheckCircle, AlertCircle, Shield, Zap, Users, ArrowRight, Sparkles } from 'lucide-react';
import { URLS } from '../config/constants';
import { getTheme, useMobile } from '../utils/theme';

const PORTAL_URL = URLS.portalScript;

function SetPassword({ setCurrentPage, darkMode, setDarkMode }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [userName] = useState('');
  const isMobile = useMobile();
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    setToken(params.get('token') || '');
    setEmail(params.get('email') || '');
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const firstName = userName ? userName.split(' ')[0] : email ? email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Team Member';

  const theme = getTheme(darkMode);
  const { accentHighlight: accentPrimary, accentMarketingAlt: accentSecondary, success: accentSuccess, gradientMarketing: accentGradient, bg: bgColor, cardBg, text: textColor, textMuted: mutedColor, borderLight: borderColor, logoLY, logoT } = theme.colors;

  const requirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'One number', test: (p) => /[0-9]/.test(p) },
  ];

  const allRequirementsMet = requirements.every(req => req.test(password));
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (!allRequirementsMet) { setError('Please meet all password requirements.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);

    try {
      const payload = { action: 'setPassword', email, token, password };
      const response = await fetch(PORTAL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      });
      const text = await response.text();
      let result;
      if (text.trim().startsWith('<')) {
        const match = text.match(/HREF="([^"]+)"/i);
        if (match) {
          const redirectUrl = match[1].replace(/&amp;/g, '&');
          const redirectResponse = await fetch(redirectUrl);
          result = JSON.parse(await redirectResponse.text());
        } else { setError('Server error. Please try again.'); setLoading(false); return; }
      } else {
        result = JSON.parse(text);
      }
      if (result.success) { setSuccess(true); } 
      else { setError(result.message || 'Failed to set password.'); }
    } catch (err) {
      console.error('Set password error:', err);
      setError('Unable to connect. Please try again.');
    }
    setLoading(false);
  };

  // ============ SUCCESS STATE ============
  if (success) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{
          backgroundColor: cardBg, borderRadius: '20px', padding: isMobile ? '40px 28px' : '56px 48px',
          maxWidth: '480px', width: '100%', textAlign: 'center',
          boxShadow: darkMode ? '0 25px 60px rgba(0,0,0,0.4)' : '0 25px 60px rgba(0,119,182,0.12)',
          border: `1px solid ${borderColor}`,
        }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%', background: accentGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', animation: 'bounceIn 0.6s ease',
          }}>
            <CheckCircle size={40} color="#ffffff" />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: textColor, margin: '0 0 12px' }}>
            You're All Set!
          </h1>
          <p style={{ fontSize: '16px', color: mutedColor, margin: '0 0 32px', lineHeight: '1.6' }}>
            Your password has been created successfully.<br/>Welcome to the lyt Communications team.
          </p>
          <button
            onClick={() => setCurrentPage('portal-login')}
            style={{
              width: '100%', padding: '16px 32px', background: accentGradient, color: '#ffffff',
              border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              transition: 'all 0.3s ease', boxShadow: `0 8px 24px ${accentPrimary}40`,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Log Into Your Portal <ArrowRight size={18} />
          </button>
        </div>
        <style>{`@keyframes bounceIn { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    );
  }

  // ============ MAIN WELCOME PAGE ============
  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor }}>
      {/* Top accent stripe */}
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #0077B6 0%, #00b4d8 50%, #28a745 100%)' }} />

      {/* Top bar */}
      <div style={{ backgroundColor: darkMode ? '#112240' : '#ffffff', padding: '6px 20px', display: 'flex', justifyContent: 'flex-end', borderBottom: `1px solid ${borderColor}` }}>
        {setDarkMode && (
          <button onClick={() => setDarkMode(!darkMode)} aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"} style={{
            backgroundColor: 'transparent', border: 'none', color: mutedColor, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '8px 12px',
            borderRadius: '6px', minHeight: '44px',
          }}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span>{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Main content */}
      <div style={{
        maxWidth: '960px', margin: '0 auto', padding: isMobile ? '32px 20px' : '48px 32px',
        opacity: animateIn ? 1 : 0, transform: animateIn ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.6s ease',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: isMobile ? '36px' : '44px', fontWeight: '800', letterSpacing: '-2px' }}>
            <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          </span>
          <span style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: '400', color: mutedColor, marginLeft: '4px', verticalAlign: 'top' }}>
            Communications
          </span>
        </div>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '40px' }}>
          <span style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
            Building Digital Futures
          </span>
        </div>

        {/* Welcome hero */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '40px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px',
            borderRadius: '20px', backgroundColor: darkMode ? 'rgba(200,80,192,0.15)' : 'rgba(0,119,182,0.08)',
            border: `1px solid ${darkMode ? 'rgba(200,80,192,0.25)' : 'rgba(0,119,182,0.15)'}`,
            marginBottom: '16px', fontSize: '13px', color: accentPrimary, fontWeight: '600',
          }}>
            <Sparkles size={14} /> New Team Member
          </div>
          <h1 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: textColor, margin: '0 0 12px', lineHeight: '1.2' }}>
            Welcome aboard, {firstName}!
          </h1>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: mutedColor, margin: '0', lineHeight: '1.6' }}>
            Set up your password to access the lyt Communications team portal.
          </p>
        </div>

        {/* Two-column layout */}
        <div style={{ display: 'flex', gap: '28px', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>

          {/* LEFT: Password form */}
          <div style={{ flex: isMobile ? 'none' : '1', width: isMobile ? '100%' : 'auto', maxWidth: '480px' }}>
            <div style={{
              backgroundColor: cardBg, borderRadius: '16px', padding: isMobile ? '28px 24px' : '36px 32px',
              boxShadow: darkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,119,182,0.08)',
              border: `1px solid ${borderColor}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px', background: accentGradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Lock size={20} color="#ffffff" />
                </div>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: textColor, margin: '0' }}>Create Your Password</h2>
                  <p style={{ fontSize: '13px', color: mutedColor, margin: '0' }}>{email || 'your email'}</p>
                </div>
              </div>

              {error && (
                <div role="alert" style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
                  backgroundColor: darkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2',
                  border: '1px solid ' + (darkMode ? 'rgba(239,68,68,0.3)' : '#fecaca'),
                  borderRadius: '10px', marginBottom: '20px', fontSize: '14px',
                  color: darkMode ? '#fca5a5' : '#dc2626',
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}
                </div>
              )}

              {/* Password */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: textColor, marginBottom: '6px' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused('password')} onBlur={() => setFocused(null)}
                    placeholder="Create a strong password"
                    style={{
                      width: '100%', padding: '14px 52px 14px 16px', fontSize: '16px',
                      border: `2px solid ${focused === 'password' ? accentPrimary : borderColor}`,
                      borderRadius: '10px', backgroundColor: darkMode ? '#0f172a' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#0d1b2a', boxSizing: 'border-box', outline: 'none',
                      transition: 'all 0.2s ease',
                      boxShadow: focused === 'password' ? `0 0 0 3px ${accentPrimary}25` : 'none',
                    }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: mutedColor,
                    padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Requirements grid */}
              <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                {requirements.map((req, i) => {
                  const passed = req.test(password);
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px',
                      color: password ? (passed ? accentSuccess : (darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8')) : mutedColor,
                      transition: 'color 0.2s ease',
                    }}>
                      <CheckCircle size={13} style={{ flexShrink: 0, opacity: password && passed ? 1 : 0.3, color: password && passed ? accentSuccess : 'currentColor' }} />
                      {req.label}
                    </div>
                  );
                })}
              </div>

              {/* Confirm */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: textColor, marginBottom: '6px' }}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocused('confirm')} onBlur={() => setFocused(null)}
                    placeholder="Confirm your password"
                    style={{
                      width: '100%', padding: '14px 52px 14px 16px', fontSize: '16px',
                      border: `2px solid ${confirmPassword ? (passwordsMatch ? accentSuccess : '#ef4444') : (focused === 'confirm' ? accentPrimary : borderColor)}`,
                      borderRadius: '10px', backgroundColor: darkMode ? '#0f172a' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#0d1b2a', boxSizing: 'border-box', outline: 'none',
                      transition: 'all 0.2s ease',
                      boxShadow: focused === 'confirm' ? `0 0 0 3px ${accentPrimary}25` : 'none',
                    }}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: mutedColor,
                    padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p style={{ fontSize: '12px', color: '#ef4444', margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <AlertCircle size={12} /> Passwords don't match
                  </p>
                )}
              </div>

              {/* Submit */}
              <button onClick={handleSubmit} disabled={loading || !allRequirementsMet || !passwordsMatch} style={{
                width: '100%', padding: '16px',
                background: allRequirementsMet && passwordsMatch ? accentGradient : (darkMode ? '#374151' : '#e2e8f0'),
                color: allRequirementsMet && passwordsMatch ? '#ffffff' : mutedColor,
                border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700',
                cursor: allRequirementsMet && passwordsMatch ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                transition: 'all 0.3s ease', opacity: loading ? 0.7 : 1,
                boxShadow: allRequirementsMet && passwordsMatch ? `0 8px 24px ${accentPrimary}35` : 'none',
              }}>
                {loading ? (
                  <><div style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#ffffff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Setting up your account...</>
                ) : (
                  <>Activate My Account <ArrowRight size={18} /></>
                )}
              </button>
            </div>
          </div>

          {/* RIGHT: Info panel */}
          <div style={{ flex: isMobile ? 'none' : '0 0 300px', width: isMobile ? '100%' : '300px', maxWidth: '480px' }}>
            <div style={{
              backgroundColor: cardBg, borderRadius: '16px', padding: '28px 24px',
              boxShadow: darkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,119,182,0.08)',
              border: `1px solid ${borderColor}`, marginBottom: '20px',
            }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: textColor, margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={16} color={accentSecondary} /> What You'll Get Access To
              </h3>
              {[
                { icon: Shield, label: 'Secure team portal', desc: 'Your personalized dashboard' },
                { icon: Users, label: 'Project assignments', desc: 'Track work in real-time' },
                { icon: Zap, label: 'Field tools', desc: 'Maps, photos, QC workflows' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: i < 2 ? '16px' : '0' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '8px',
                    backgroundColor: darkMode ? 'rgba(200,80,192,0.12)' : 'rgba(0,119,182,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <item.icon size={16} color={accentPrimary} />
                  </div>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: textColor, margin: '0 0 2px' }}>{item.label}</p>
                    <p style={{ fontSize: '12px', color: mutedColor, margin: '0' }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Help card */}
            <div style={{
              backgroundColor: darkMode ? 'rgba(200,80,192,0.08)' : 'rgba(0,119,182,0.05)',
              borderRadius: '16px', padding: '24px',
              border: `1px solid ${darkMode ? 'rgba(200,80,192,0.15)' : 'rgba(0,119,182,0.1)'}`,
            }}>
              <p style={{ fontSize: '14px', fontWeight: '600', color: textColor, margin: '0 0 8px' }}>Need help?</p>
              <p style={{ fontSize: '13px', color: mutedColor, margin: '0 0 4px', lineHeight: '1.5' }}>Contact us anytime:</p>
              <p style={{ fontSize: '14px', color: accentPrimary, margin: '0', fontWeight: '600' }}>(888) 503-9460</p>
              <p style={{ fontSize: '13px', color: accentSecondary, margin: '4px 0 0' }}>info@lytcomm.com</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: isMobile ? '32px' : '48px', paddingTop: '24px', borderTop: `1px solid ${borderColor}` }}>
          <p style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8', margin: '0' }}>
            lyt Communications · Professional Fiber Optic Construction · Webster, TX
          </p>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default SetPassword;
